import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import { CARD_COLORS } from "../../lib/cardColors";

/**
 * "Add colour" popover for a task card.
 *
 * Two ways to choose:
 *   • Presets  — the curated swatch grid.
 *   • Spectrum — three RGB sliders, giving the full 24-bit range.
 *
 * The colour is applied as a bar down the card's RIGHT EDGE rather than as a
 * background, so it never competes with the card's text and the whole range
 * is usable — including reds, greens and blues, which the old hue-arc
 * spectrum had to exclude when it painted the card body.
 *
 * Portalled to <body> and positioned from the trigger's viewport rect, so it
 * is never clipped by the modal's overflow or stacking context.
 */
const PANEL_WIDTH = 268;
// The three channels, in the order the sliders are shown.
const RGB_CHANNELS = [
  { key: "r", label: "Red" },
  { key: "g", label: "Green" },
  { key: "b", label: "Blue" },
];

// "#rrggbb" → { r, g, b }, or null when the value is missing or malformed.
// Three-digit shorthand ("#abc") is expanded, since a hand-typed colour could
// arrive that way.
const hexToRgb = (hex) => {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) => {
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(Number(n) || 0)));
  return `#${[r, g, b].map((n) => clamp(n).toString(16).padStart(2, "0")).join("")}`;
};

const ColorPicker = ({ anchorRect, value, onSelect, onClose }) => {
  const panelRef = useRef(null);
  const [tab, setTab] = useState("presets");

  // Seed the sliders from the current value, so reopening the picker on a
  // coloured card starts where that card already is.
  const seeded = useMemo(() => hexToRgb(value) || { r: 96, g: 165, b: 250 }, [
    value,
  ]);

  const [rgb, setRgb] = useState(seeded);
  const rgbHex = rgbToHex(rgb);

  // The track for one channel: that channel swept 0→255 while the other two
  // hold their current values, so each slider previews its own effect.
  const channelGradient = (key) => {
    const at = (v) => rgbToHex({ ...rgb, [key]: v });
    return `linear-gradient(to right, ${at(0)}, ${at(128)}, ${at(255)})`;
  };

  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!anchorRect) return null;

  const left = Math.max(
    8,
    Math.min(anchorRect.left, window.innerWidth - PANEL_WIDTH - 8),
  );
  const top = anchorRect.bottom + 8;

  const tabClass = (name) =>
    `flex-1 text-[12px] py-1.5 rounded-md transition-colors ${
      tab === name
        ? "bg-white text-gray-900 shadow-sm font-medium"
        : "text-gray-500 hover:text-gray-700"
    }`;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Choose card colour"
      className="fixed z-[100] rounded-xl bg-white p-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100"
      style={{ top, left, width: PANEL_WIDTH }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[13px] font-semibold text-gray-800">Card colour</p>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close colour picker"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-3">
        <button type="button" className={tabClass("presets")} onClick={() => setTab("presets")}>
          Presets
        </button>
        <button type="button" className={tabClass("spectrum")} onClick={() => setTab("spectrum")}>
          Spectrum
        </button>
      </div>

      {tab === "presets" ? (
        <div className="grid grid-cols-4 gap-2">
          {CARD_COLORS.map((c) => {
            const selected = value === c.hex;
            return (
              <button
                key={c.hex}
                type="button"
                title={c.name}
                aria-label={c.name}
                aria-pressed={selected}
                onClick={() => onSelect(c.hex)}
                className={`h-9 w-9 rounded-lg flex items-center justify-center transition-transform hover:scale-105 ${
                  selected ? "ring-2 ring-offset-2 ring-gray-800" : "ring-1 ring-black/10"
                }`}
                style={{ backgroundColor: c.hex }}
              >
                {selected && <Check size={15} className="text-gray-800" />}
              </button>
            );
          })}
        </div>
      ) : (
        <div>
          {/* Live preview of the current RGB selection, shown the way the
              card will use it: a colour bar down the right edge. */}
          <div className="h-12 w-full rounded-lg ring-1 ring-black/10 mb-3 flex items-center justify-center bg-white relative overflow-hidden">
            <span className="text-[11px] text-gray-700 font-medium tracking-wide">
              {rgbHex.toUpperCase()}
            </span>
            <span
              className="absolute inset-y-0 right-0 w-[6px]"
              style={{ backgroundColor: rgbHex }}
            />
          </div>

          {/* One slider per channel. Each track previews its own axis: moving
              red shows what red does at the current green and blue. */}
          {RGB_CHANNELS.map(({ key, label }) => (
            <div key={key} className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <label
                  htmlFor={`qh-rgb-${key}`}
                  className="text-[11px] text-gray-500"
                >
                  {label}
                </label>
                <span className="text-[11px] text-gray-400 tabular-nums">
                  {rgb[key]}
                </span>
              </div>
              <input
                id={`qh-rgb-${key}`}
                type="range"
                min={0}
                max={255}
                value={rgb[key]}
                onChange={(e) =>
                  setRgb((prev) => ({
                    ...prev,
                    [key]: Number(e.target.value),
                  }))
                }
                aria-label={label}
                className="qh-spectrum-range w-full h-3 rounded-full cursor-pointer"
                style={{ background: channelGradient(key) }}
              />
            </div>
          ))}

          <button
            type="button"
            onClick={() => onSelect(rgbHex)}
            className="w-full rounded-lg bg-[#4253C7] py-2 text-[12px] font-medium text-white hover:opacity-90"
          >
            Apply this colour
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => onSelect(null)}
        className="mt-3 w-full rounded-lg border border-gray-200 py-2 text-[12px] text-gray-600 hover:bg-gray-50"
      >
        No colour (default)
      </button>
    </div>,
    document.body,
  );
};

export default ColorPicker;
